/* MOTURO delivery estimate · 0.1.0 (2026-10-07, delivery-native12h)
   Expected delivery window for Germany on the product page, written into the snippet moturo-delivery below the price
   row. Liquid decides whether the block exists at all (localization country DE) and whether it starts hidden (can_buy of
   the selected variant). This script only (1) replaces the static business-day sentence with concrete dates and
   (2) follows the variant select through the option data written by Liquid (data-available already carries the GPSR,
   staging, price and inventory gates). It never enables a purchase and never touches the form, the price or the
   availability line.
   Calendar: processing base = today when today is a business day and the Berlin wall clock is before 12:00, otherwise
   the next business day (weekend or holiday: the next business day, no second delay). Delivery window = base + 1 ..
   base + 3 business days. Business days are Monday to Friday without the statutory holidays of the shipping origin
   (Thuringia: the nine nationwide holidays, Reformationstag 31 October, Weltkindertag 20 September since 2019); the
   Easter-bound holidays are computed for any year, nothing is guessed or clamped. Dates are calendar arithmetic on day
   numbers; the Berlin wall clock comes from Intl with timeZone Europe/Berlin, so CET/CEST switches cannot move the
   cutoff. The text is rendered again at the next Berlin 12:00 or 00:00 boundary, when the tab becomes visible again and
   after a back/forward-cache restore. Without Intl time zone support the static sentence stays. The checkout delivery
   date remains decisive; this block is an estimate. */
(function (root) {
  'use strict';
  const ZONE = 'Europe/Berlin', CUTOFF_HOUR = 12, DAY = 864e5, MIN_DELAY = 1000, MAX_DELAY = 2147483647;
  const MARK_FROM = '{{ from }}', MARK_TO = '{{ to }}';
  const q = (selector, scope) => Array.prototype.slice.call((scope || root.document).querySelectorAll(selector));
  const pad = n => (n < 10 ? '0' : '') + n;

  /* Calendar days as integers (days since 1970-01-01, UTC), so no local time zone can shift a date. */
  const dayOf = (year, month, day) => Math.round(Date.UTC(year, month - 1, day) / DAY);
  function ymd(day) { const t = new Date(day * DAY); return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), w: t.getUTCDay() }; }
  const iso = day => { const p = ymd(day); return p.y + '-' + pad(p.m) + '-' + pad(p.d); };
  const dayFromIso = text => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(text)); return m ? dayOf(+m[1], +m[2], +m[3]) : NaN; };

  /* Easter Sunday (Gregorian, anonymous algorithm after Meeus/Jones/Butcher) as a day number. */
  function easter(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
    return dayOf(year, month, day);
  }

  /* Statutory holidays at the shipping origin (Thuringia). Corpus Christi is not a statewide Thuringian holiday and is
     not modelled; 24 and 31 December are ordinary business days here (carrier schedules stay with the checkout). */
  const HOLIDAYS = {};
  function holidays(year) {
    if (HOLIDAYS[year]) return HOLIDAYS[year];
    const sunday = easter(year);
    const set = new Set([
      dayOf(year, 1, 1),      // Neujahr
      sunday - 2,             // Karfreitag
      sunday + 1,             // Ostermontag
      dayOf(year, 5, 1),      // Tag der Arbeit
      sunday + 39,            // Christi Himmelfahrt
      sunday + 50,            // Pfingstmontag
      dayOf(year, 10, 3),     // Tag der Deutschen Einheit
      dayOf(year, 10, 31),    // Reformationstag (Thüringen)
      dayOf(year, 12, 25),    // 1. Weihnachtstag
      dayOf(year, 12, 26)     // 2. Weihnachtstag
    ]);
    if (year >= 2019) set.add(dayOf(year, 9, 20)); // Weltkindertag (Thüringen, seit 2019)
    HOLIDAYS[year] = set;
    return set;
  }
  function isBusinessDay(day) { const p = ymd(day); return p.w >= 1 && p.w <= 5 && !holidays(p.y).has(day); }
  function nextBusinessDay(day) { let d = day + 1; while (!isBusinessDay(d)) d += 1; return d; }
  function addBusinessDays(day, n) { let d = day; for (let i = 0; i < n; i += 1) d = nextBusinessDay(d); return d; }

  /* Berlin wall clock of an instant (Intl handles CET/CEST). Throws when the engine has no time zone data. */
  let wallFormat = null;
  function wall(ms) {
    if (!wallFormat) wallFormat = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
    const v = {};
    wallFormat.formatToParts(new Date(ms)).forEach(part => { if (part.type !== 'literal') v[part.type] = parseInt(part.value, 10); });
    if (v.hour === 24) v.hour = 0;
    if (![v.year, v.month, v.day, v.hour, v.minute, v.second].every(Number.isFinite)) throw new Error('wall clock unavailable');
    return { year: v.year, month: v.month, day: v.day, hour: v.hour, minute: v.minute, second: v.second, dayIndex: dayOf(v.year, v.month, v.day) };
  }

  /* Processing base and delivery window for an instant. */
  function estimate(ms) {
    const t = wall(ms), today = t.dayIndex, businessDay = isBusinessDay(today), beforeCutoff = t.hour < CUTOFF_HOUR;
    const base = businessDay && beforeCutoff ? today : nextBusinessDay(today);
    return { today: iso(today), businessDay, beforeCutoff, base: iso(base), from: iso(addBusinessDays(base, 1)), to: iso(addBusinessDays(base, 3)) };
  }

  /* Instant of the next Berlin 12:00 (before the cutoff) or 00:00 (after it); the wall clock is read back so that the
     offset in force at the target is used, also across a CET/CEST switch. */
  function nextBoundary(ms) {
    const t = wall(ms), before = t.hour < CUTOFF_HOUR;
    const target = (before ? t.dayIndex : t.dayIndex + 1) * DAY + (before ? CUTOFF_HOUR : 0) * 36e5;
    let guess = target;
    for (let i = 0; i < 4; i += 1) {
      const w = wall(guess), seen = w.dayIndex * DAY + (w.hour * 3600 + w.minute * 60 + w.second) * 1e3, diff = seen - target;
      if (diff === 0) break;
      guess -= diff;
    }
    return guess;
  }

  /* Native date wording of the page language; the year only when it differs from the current Berlin year. */
  function label(day, lang, currentYear) {
    const p = ymd(day), options = { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' };
    if (p.y !== currentYear) options.year = 'numeric';
    return new Intl.DateTimeFormat(lang || undefined, options).format(new Date(day * DAY + 12 * 36e5));
  }

  function render(el, ms) {
    if (!el || el.getAttribute('data-delivery-country') !== 'DE') return false;
    const template = el.getAttribute('data-delivery-dates') || '', target = el.querySelector('[data-delivery-estimate]');
    if (!target || template.indexOf(MARK_FROM) < 0 || template.indexOf(MARK_TO) < 0) return false;
    const now = typeof ms === 'number' ? ms : api.now();
    const e = estimate(now), year = wall(now).year;
    const lang = el.getAttribute('data-delivery-lang') || (root.document && root.document.documentElement && root.document.documentElement.lang) || 'de';
    target.textContent = template.split(MARK_FROM).join(label(dayFromIso(e.from), lang, year)).split(MARK_TO).join(label(dayFromIso(e.to), lang, year));
    el.setAttribute('data-delivery-from', e.from);
    el.setAttribute('data-delivery-to', e.to);
    el.setAttribute('data-delivery-mode', 'dates');
    return true;
  }

  /* Purchasability of the selected variant from the option data of the existing select; null without a select (single
     variant), then the hidden state rendered by Liquid is final. */
  function purchasable(el) {
    const scope = (el.closest && el.closest('[data-product]')) || root.document, select = scope ? scope.querySelector('[data-variant-select]') : null;
    if (!select) return null;
    const option = (select.selectedOptions && select.selectedOptions[0]) || (select.options && select.options[select.selectedIndex]) || null;
    return !!option && option.dataset.available === 'true';
  }
  function update(el) {
    const ok = purchasable(el);
    if (ok !== null) el.hidden = !ok;
    if (el.hidden) return false;
    try { return render(el); } catch (_) { el.setAttribute('data-delivery-mode', 'static'); return false; }
  }

  let timer = null;
  function schedule() {
    if (typeof root.setTimeout !== 'function') return;
    if (timer !== null) root.clearTimeout(timer);
    timer = null;
    let delay;
    try { const now = api.now(); delay = nextBoundary(now) - now + MIN_DELAY; } catch (_) { return; }
    timer = root.setTimeout(refresh, Math.max(MIN_DELAY, Math.min(delay, MAX_DELAY)));
  }
  function refresh() {
    if (!root.document) return;
    q('[data-delivery]').forEach(update);
    schedule();
  }
  function bind() {
    const doc = root.document;
    if (!doc) return;
    q('[data-delivery]', doc).forEach(el => {
      if (el.getAttribute('data-delivery-bound') === 'true') return;
      el.setAttribute('data-delivery-bound', 'true');
      const scope = (el.closest && el.closest('[data-product]')) || doc, select = scope.querySelector('[data-variant-select]');
      if (select) select.addEventListener('change', () => { update(el); });
    });
    refresh();
  }
  function bindDom() {
    const doc = root.document;
    doc.addEventListener('visibilitychange', () => { if (doc.visibilityState === 'visible') refresh(); });
    if (typeof root.addEventListener === 'function') root.addEventListener('pageshow', event => { if (event && event.persisted) refresh(); });
    doc.addEventListener('shopify:section:load', bind);
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', bind); else bind();
  }

  const api = { ZONE, CUTOFF_HOUR, now: () => Date.now(), easter, holidays, isBusinessDay, estimate, nextBoundary, wall, label, render, update, bind, refresh, dayOf, iso };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root.MoturoDelivery) { if (root.document) root.MoturoDelivery.bind(); return; }
  root.MoturoDelivery = api;
  if (root.document && typeof root.document.addEventListener === 'function') bindDom();
})(typeof window !== 'undefined' ? window : globalThis);
